import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { challengesAPI } from '../../api';
import { Card, Button, Input, Select, Modal, Badge, ProgressBar, Spinner } from '../../components/ui';
import { Target, Plus } from 'lucide-react';

export const ChallengesPage = () => {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '',
    start_date: new Date().toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    goal_type: 'sessions_count', goal_value: 5, reward_points: 100
  });

  const loadData = useCallback(async () => {
    try {
      const [challengesRes] = await Promise.all([challengesAPI.list()]);
      setChallenges(challengesRes.data);
      
      if (user?.role === 'client') {
        const enrollmentsRes = await challengesAPI.getEnrollments();
        setEnrollments(enrollmentsRes.data);
      }
    } catch (err) {
      console.error('Failed to load challenges:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    try {
      await challengesAPI.create({
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString()
      });
      setShowModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create challenge');
    }
  };

  const handleEnroll = async (challengeId) => {
    try {
      await challengesAPI.enroll(challengeId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to enroll');
    }
  };

  const enrolledIds = enrollments.map(e => e.challenge_id);
  const isAdmin = user?.role === 'admin';

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="challenges-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Challenges</h1>
          <p className="text-gray-500">
            {isAdmin ? 'Create and manage challenges' : 'Join challenges to earn bonus points'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Challenge
          </Button>
        )}
      </div>

      {/* My Progress (Client) */}
      {user?.role === 'client' && enrollments.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">My Challenges</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {enrollments.map(e => (
              <Card key={e.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">{e.challenge_name}</h4>
                    <p className="text-sm text-gray-500">
                      {e.progress} / {e.goal_value} completed
                    </p>
                  </div>
                  {e.is_completed ? (
                    <Badge variant="success">Complete!</Badge>
                  ) : (
                    <span className="text-sm font-medium text-primary-600">
                      {Math.round((e.progress / e.goal_value) * 100)}%
                    </span>
                  )}
                </div>
                <ProgressBar value={e.progress} max={e.goal_value} />
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Available Challenges */}
      <div>
        <h3 className="font-semibold mb-3">Available Challenges</h3>
        {challenges.length === 0 ? (
          <Card className="p-8 text-center">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No active challenges</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {challenges.map(challenge => {
              const isEnrolled = enrolledIds.includes(challenge.id);
              const daysLeft = Math.ceil((new Date(challenge.end_date) - new Date()) / (1000 * 60 * 60 * 24));
              
              return (
                <Card key={challenge.id} className="overflow-hidden">
                  <div className="h-24 bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Target className="w-10 h-10 text-white/80" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold">{challenge.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{challenge.description}</p>
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Goal:</span>
                        <span className="font-medium">{challenge.goal_value} {challenge.goal_type?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Reward:</span>
                        <span className="font-medium text-primary-600">{challenge.reward_points} pts</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Time left:</span>
                        <span className="font-medium">{daysLeft > 0 ? `${daysLeft} days` : 'Ended'}</span>
                      </div>
                    </div>
                    {user?.role === 'client' && (
                      <Button 
                        className="w-full mt-4" 
                        variant={isEnrolled ? 'secondary' : 'primary'}
                        disabled={isEnrolled}
                        onClick={() => handleEnroll(challenge.id)}
                      >
                        {isEnrolled ? 'Enrolled' : 'Join Challenge'}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Challenge Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Create Challenge">
        <form onSubmit={handleCreateChallenge} className="space-y-4">
          <Input label="Challenge Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Date" type="datetime-local" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required />
            <Input label="End Date" type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} required />
          </div>
          <Select
            label="Goal Type"
            value={form.goal_type}
            onChange={e => setForm({ ...form, goal_type: e.target.value })}
            options={[
              { value: 'sessions_count', label: 'Number of Sessions' },
              { value: 'total_duration', label: 'Total Minutes' },
              { value: 'streak_days', label: 'Streak Days' },
            ]}
          />
          <Input label="Goal Value" type="number" value={form.goal_value} onChange={e => setForm({ ...form, goal_value: parseInt(e.target.value) })} min={1} required />
          <Input label="Reward Points" type="number" value={form.reward_points} onChange={e => setForm({ ...form, reward_points: parseInt(e.target.value) })} min={1} required />
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1">Create Challenge</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ChallengesPage;
